import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "../api/ApiClientContext.js";
import { listItems } from "../api/items.js";
import type { WireItem } from "../api/types.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Candidates for the swap flow: this customer's other stored (not yet
 * picked up) items, across every season — a customer's earlier stored set
 * keeps whatever `seasonId` it was created under forever, so a search
 * scoped to "the current season" would miss anything stored before the last
 * season close.
 *
 * Matches `@skladiscenje/core`'s `isSameCustomer` (`swap.ts`) exactly —
 * lastname + name + carMaker, case-insensitively, no registration-number
 * check — rather than legacy's stricter `filterCustomer`
 * (AddItemWindow.xaml.cs:205-289, which also tolerated a null registration
 * number). Deliberate: the server enforces `isSameCustomer` as the
 * authoritative rule at save time regardless of what this list shows, so
 * showing a candidate here that the server would then reject on submit
 * would be a worse outcome than the reverse.
 */
export function useSwapCandidates(lastname: string, name: string, carMaker: string, enabled: boolean): WireItem[] {
  const client = useApiClient();
  const searchText = useDebouncedValue(`${lastname} ${name}`.trim(), 300);
  const debouncedCarMaker = useDebouncedValue(normalize(carMaker), 300);

  const { data } = useQuery({
    queryKey: ["swap-candidates", searchText],
    queryFn: () => listItems(client, { pickedUp: false, search: searchText, limit: 25, offset: 0 }),
    enabled: enabled && searchText.length > 0,
    placeholderData: (previous) => previous,
  });

  if (!enabled || !debouncedCarMaker) return [];

  const lastnameNorm = normalize(lastname);
  const nameNorm = normalize(name);
  if (!lastnameNorm || !nameNorm) return [];

  return (data?.items ?? []).filter((item) => {
    const itemLastname = item.lastname ? normalize(item.lastname) : null;
    const itemName = item.name ? normalize(item.name) : null;
    const itemCarMaker = item.carMaker ? normalize(item.carMaker) : null;
    return itemLastname === lastnameNorm && itemName === nameNorm && itemCarMaker === debouncedCarMaker;
  });
}
